import { observer } from "mobx-react-lite"
import { formatUnits } from "viem"

import type { IncreasePositionStore } from "@/domains/thick-v1/stores/increase-position-store"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { useCreateNfpPositionLens } from "@/hooks/use-create-nfp-position-lens"
import { THICK_V1_BASE_ADDRESSES } from "@/domains/thick-v1/constants"

import { IncreasePositionView } from "./increase-position-view"

const formatBalance = (wei: bigint, decimals: number): string => {
  try {
    return formatUnits(wei, decimals)
  } catch {
    return "0"
  }
}

/**
 * Observer composite for `IncreasePositionStore`.
 *
 * Pulls the active position off the store base, reads live balance +
 * allowance state via `useCreateNfpPositionLens`, pre-formats every
 * derived string / number, and hands the view a plain DTO. The composite
 * owns no mutable state — the store is the single source of truth.
 *
 * @todo MIGRATE: useCreateNfpPositionLens is still a React-Query hook. Once
 * the domain has an AllowanceRepository, switch both this composite and the
 * interaction-level approval fallback to pull from there.
 */
export const IncreasePositionComposite = observer(
  ({ store }: { store: IncreasePositionStore }) => {
    const scaleApp = useScaleApplication()
    const status = store.resolvedStatus

    const walletAddress = scaleApp.Wallet.Connection.connectedAddress?.raw

    const token0Address =
      status.kind === "found" ? (status.position.pool.token0.address.raw as `0x${string}`) : undefined
    const token1Address =
      status.kind === "found" ? (status.position.pool.token1.address.raw as `0x${string}`) : undefined
    const token0Decimals =
      status.kind === "found" ? status.position.pool.token0.decimals : 18
    const token1Decimals =
      status.kind === "found" ? status.position.pool.token1.decimals : 18

    const { createNfpPositionLens } = useCreateNfpPositionLens({
      account: walletAddress,
      chainId: scaleApp.Config.chainId,
      nfpManagerAddress: THICK_V1_BASE_ADDRESSES.nfPositionManager,
      token0Address,
      token1Address,
      token0Decimals,
      token1Decimals,
    })

    if (status.kind !== "found") {
      return (
        <div className="rounded-lg border border-p400-light bg-p400 p-4 text-t300 text-sm">
          {status.kind === "loading" && "Loading position..."}
          {status.kind === "missing" &&
            "This position was not found or is not owned by the connected wallet."}
          {status.kind === "idle" && "Select a position to continue."}
        </div>
      )
    }

    const position = status.position
    const pool = position.pool
    const token0 = pool.token0
    const token1 = pool.token1

    const amount0Parsed = Number(store.amount0) || 0
    const amount1Parsed = Number(store.amount1) || 0
    const totalUsd = amount0Parsed * token0.bestPrice + amount1Parsed * token1.bestPrice

    return (
      <IncreasePositionView
        token0Symbol={token0.symbol}
        token1Symbol={token1.symbol}
        displayMidPrice={pool.displayMidPrice}
        displayRange={position.displayRange}
        isInRange={position.isInRange}
        amount0={store.amount0}
        amount1={store.amount1}
        onChangeAmount0={(value) => store.setAmount0FromUserInput(value)}
        onChangeAmount1={(value) => store.setAmount1FromUserInput(value)}
        onFocusAmount0={() => store.setActiveDepositInput("token0")}
        onFocusAmount1={() => store.setActiveDepositInput("token1")}
        balance0Display={formatBalance(
          createNfpPositionLens.token0.balance.wei,
          token0.decimals,
        )}
        balance1Display={formatBalance(
          createNfpPositionLens.token1.balance.wei,
          token1.decimals,
        )}
        onUseBalance0={() =>
          store.setAmount0FromUserInput(
            formatBalance(createNfpPositionLens.token0.balance.wei, token0.decimals),
          )
        }
        onUseBalance1={() =>
          store.setAmount1FromUserInput(
            formatBalance(createNfpPositionLens.token1.balance.wei, token1.decimals),
          )
        }
        totalUsd={totalUsd}
        slippagePercent={store.slippage}
        canSubmit={store.canSubmit}
        isSubmitting={store.isSubmitting}
        submissionError={store.submissionError}
        onSubmit={() => void store.submit({ lens: createNfpPositionLens })}
        nfpmTokenIdPending={position.nfpmTokenIdPending}
      />
    )
  },
)
